import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import { Donator } from "../../models/Donator";
import {
    getDonatorsForAdmin,
    addDonation,
    updateDonation,
    deleteDonation,
} from "../../services/DonatorService";
import Loading from "../../components/Loading";
import TeamTierSection from "./TeamTierSection";

interface AggregatedDonor {
    key: string;
    steamId: string | null;
    name: string;
    total: number;
    rank: number;
    donations: Donator[];
}

const card: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "16px 20px",
    minWidth: 120,
    textAlign: "center",
};

const inputStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,102,0,0.2)",
    borderRadius: 6,
    color: "rgba(255,255,255,0.9)",
    padding: "8px 12px",
};

const label: React.CSSProperties = {
    display: "block",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "rgba(255,255,255,0.55)",
    marginBottom: 4,
};

const premiumBadge: React.CSSProperties = {
    background: "rgba(255,153,51,0.18)", color: "#ff9933",
    borderRadius: 12, padding: "2px 10px", fontSize: "0.7rem", fontWeight: 700,
};

// Local calendar date (YYYY-MM-DD). Avoid toISOString(), which reports the UTC day
// and is off by one for a Danish admin adding a donation shortly after local midnight.
const today = () => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
};

// Amounts are stored as whole kroner (INT). Reject decimals / thousands separators / junk
// instead of silently truncating with parseInt ("1.000" -> 1, "150.5" -> 150).
const parseAmount = (s: string): number | null => {
    const t = s.trim();
    if (!/^\d+$/.test(t)) return null;
    const n = parseInt(t, 10);
    return n > 0 ? n : null;
};

const AdminDonatorsPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    const [donators, setDonators] = useState<Donator[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);

    // Add form
    const [showAdd, setShowAdd] = useState(false);
    const [addSteamId, setAddSteamId] = useState("");
    const [addName, setAddName] = useState("");
    const [addAmount, setAddAmount] = useState("");
    const [addDate, setAddDate] = useState(today());
    const [addFlag, setAddFlag] = useState(true);

    // Inline edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editSteamId, setEditSteamId] = useState("");
    const [editName, setEditName] = useState("");
    const [editAmount, setEditAmount] = useState("");
    const [editDate, setEditDate] = useState("");
    const [editOrigCreatedAt, setEditOrigCreatedAt] = useState("");

    const reload = () => {
        getDonatorsForAdmin()
            .then((d) => { setDonators(d); setError(null); })
            .catch(() => setError("Could not load donations."));
    };

    useEffect(() => {
        if (!isAdmin) return;
        reload();
    }, [isAdmin]);

    const aggregated = useMemo(() => {
        if (!donators) return [];
        const map = new Map<string, AggregatedDonor>();
        for (const d of donators) {
            // Only merge donations that share a real SteamID. Anonymous donations (no SteamID)
            // stay separate — two different people both named "Anonym" must not be combined.
            const key = d.steamId || `anon:${d.id}`;
            const existing = map.get(key);
            if (existing) {
                existing.total += d.donationAmount;
                existing.donations.push(d);
            } else {
                map.set(key, { key, steamId: d.steamId, name: d.name, total: d.donationAmount, rank: 0, donations: [d] });
            }
        }
        const list = Array.from(map.values());
        list.forEach((g) => g.donations.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        list.sort((a, b) => b.total - a.total);
        list.forEach((g, i) => { g.rank = i; });
        return list;
    }, [donators]);

    const counts = useMemo(() => ({
        donors: aggregated.length,
        total: aggregated.reduce((s, g) => s + g.total, 0),
        premium: aggregated.filter((g) => g.total >= 200).length,
    }), [aggregated]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return aggregated;
        return aggregated.filter((g) =>
            g.name.toLowerCase().includes(q) || (g.steamId ?? "").includes(q));
    }, [aggregated, search]);

    const toggle = (key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const resetAdd = () => {
        setAddSteamId(""); setAddName(""); setAddAmount(""); setAddDate(today()); setAddFlag(true);
    };

    const submitAdd = async () => {
        const amount = parseAmount(addAmount);
        if (!addName.trim()) { setError("Name is required."); return; }
        if (amount === null) { setError("Amount must be a positive whole number of kroner."); return; }
        setBusy(true); setError(null); setNotice(null);
        try {
            const steamId = addSteamId.trim() || null;
            const wantFlag = addFlag && !!steamId;
            const res = await addDonation({
                steamId,
                name: addName.trim(),
                amount,
                createdAt: addDate ? `${addDate}T00:00:00` : null,
                setDonatorFlag: wantFlag,
            });
            resetAdd();
            setShowAdd(false);
            if (wantFlag && !res.donatorFlagSet) {
                setNotice(`Donation saved, but no account exists yet for SteamID ${steamId}, so the donator flag couldn't be set. It won't apply automatically once they log in — set it from the Users page after they've signed in.`);
            }
            reload();
        } catch (e) {
            setError("Failed to add donation. Are you logged in as an admin?");
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (d: Donator) => {
        setEditingId(d.id);
        setEditSteamId(d.steamId ?? "");
        setEditName(d.name);
        setEditAmount(String(d.donationAmount));
        setEditDate(d.createdAt ? d.createdAt.slice(0, 10) : today());
        setEditOrigCreatedAt(d.createdAt ?? "");
    };

    const submitEdit = async (id: number) => {
        const amount = parseAmount(editAmount);
        if (!editName.trim()) { setError("Name is required."); return; }
        if (amount === null) { setError("Amount must be a positive whole number of kroner."); return; }
        setBusy(true); setError(null);
        try {
            // Preserve the original timestamp (incl. time-of-day) when the admin didn't change the
            // date, so editing name/amount doesn't silently reset created_at to midnight.
            const dateChanged = editDate !== editOrigCreatedAt.slice(0, 10);
            const createdAt = editDate
                ? (dateChanged ? `${editDate}T00:00:00` : (editOrigCreatedAt || `${editDate}T00:00:00`))
                : null;
            await updateDonation(id, {
                steamId: editSteamId.trim() || null,
                name: editName.trim(),
                amount,
                createdAt,
            });
            setEditingId(null);
            reload();
        } catch (e) {
            setError("Failed to update donation.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id: number, name: string) => {
        if (!window.confirm(`Delete this donation from ${name}? This cannot be undone.`)) return;
        setBusy(true); setError(null);
        try {
            await deleteDonation(id);
            reload();
        } catch (e) {
            setError("Failed to delete donation.");
        } finally {
            setBusy(false);
        }
    };

    if (!authLoaded) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;
    if (!isAdmin) return <div className="container mt-4"><div className="alert alert-danger">Admins only.</div></div>;
    if (!donators && !error) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;

    return (
        <div className="container mt-4" style={{ color: "var(--text-primary)" }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                    <h1 style={{ color: "#e2e8f0", fontWeight: 700 }}>Donator Admin</h1>
                    <p style={{ color: "rgba(255,255,255,0.6)" }}>Add and edit donations. Amounts drive the Hall of Fame, tiers and bonus clips.</p>
                </div>
                <button
                    className="btn btn-warning text-dark fw-bold"
                    onClick={() => { setShowAdd((s) => !s); setError(null); }}
                    style={{ height: "fit-content" }}
                >
                    {showAdd ? "Close" : "+ Add donation"}
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-warning" onClick={() => setNotice(null)} style={{ cursor: "pointer" }}>{notice}</div>}

            {/* Add form */}
            {showAdd && (
                <div style={{ ...card, textAlign: "left", padding: "20px 22px", margin: "8px 0 20px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                        <div>
                            <label style={label}>SteamID (optional)</label>
                            <input style={{ ...inputStyle, width: "100%" }} value={addSteamId}
                                onChange={(e) => setAddSteamId(e.target.value)} placeholder="7656119…" />
                        </div>
                        <div>
                            <label style={label}>Name *</label>
                            <input style={{ ...inputStyle, width: "100%" }} value={addName}
                                onChange={(e) => setAddName(e.target.value)} placeholder="Display name" />
                        </div>
                        <div>
                            <label style={label}>Amount (kr) *</label>
                            <input style={{ ...inputStyle, width: "100%" }} type="number" min={1} value={addAmount}
                                onChange={(e) => setAddAmount(e.target.value)} placeholder="100" />
                        </div>
                        <div>
                            <label style={label}>Date</label>
                            <input style={{ ...inputStyle, width: "100%" }} type="date" value={addDate}
                                onChange={(e) => setAddDate(e.target.value)} />
                        </div>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-3" style={{ marginTop: 16 }}>
                        <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" id="add-donator-flag"
                                checked={addFlag && !!addSteamId.trim()}
                                disabled={!addSteamId.trim()}
                                onChange={(e) => setAddFlag(e.target.checked)} />
                            <label className="form-check-label" htmlFor="add-donator-flag"
                                style={{ color: addSteamId.trim() ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)", fontSize: "0.9rem" }}>
                                Also mark account as donator
                                {!addSteamId.trim() && " (needs a SteamID)"}
                            </label>
                        </div>
                        <button className="btn btn-warning text-dark fw-bold ms-auto" disabled={busy} onClick={submitAdd}>
                            {busy ? "Saving…" : "Save donation"}
                        </button>
                    </div>
                </div>
            )}

            {/* Summary */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "8px 0 20px" }}>
                {[
                    { label: "Donors", value: counts.donors, color: "#e2e8f0" },
                    { label: "Total raised", value: `${counts.total} kr`, color: "#ff9933" },
                    { label: "Premium (≥200)", value: counts.premium, color: "#4ade80" },
                ].map((s) => (
                    <div key={s.label} style={card}>
                        <div style={{ fontSize: "1.6rem", fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Search */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                <input
                    placeholder="Search name or SteamID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ ...inputStyle, flex: "1 1 240px", maxWidth: 360 }}
                />
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>{filtered.length} shown</span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", background: "linear-gradient(135deg,#1a1a1a 0%,#252525 100%)", border: "1px solid rgba(255,102,0,0.1)", borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#e2e8f0" }}>
                    <thead style={{ background: "linear-gradient(135deg,#2a2a2a 0%,#333 100%)", borderBottom: "2px solid rgba(255,102,0,0.15)" }}>
                        <tr>
                            {["", "#", "Donor", "SteamID", "Total", "Entries"].map((h, i) => (
                                <th key={i} style={{ padding: "12px 16px", textAlign: i >= 4 ? "right" : "left", textTransform: "uppercase", fontSize: 13, letterSpacing: "0.5px", color: "rgba(255,255,255,0.9)" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((g, i) => {
                            const rank = g.rank;
                            const isOpen = expanded.has(g.key);
                            return (
                                <React.Fragment key={g.key}>
                                    <tr
                                        style={{ background: i % 2 ? "rgba(36,36,36,0.5)" : "rgba(42,42,42,0.5)", cursor: "pointer" }}
                                        onClick={() => toggle(g.key)}
                                    >
                                        <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.5)", width: 30 }}>{isOpen ? "▾" : "▸"}</td>
                                        <td style={{ padding: "10px 16px", fontWeight: 700, color: "#ff9933" }}>{rank === 0 ? "👑" : rank + 1}</td>
                                        <td style={{ padding: "10px 16px" }}>
                                            {g.steamId ? (
                                                <Link to={`/player/${g.steamId}`} onClick={(e) => e.stopPropagation()} style={{ color: "#ff9933", fontWeight: 600, textDecoration: "none" }}>
                                                    {g.name}
                                                </Link>
                                            ) : (
                                                <span style={{ fontWeight: 600 }}>{g.name}</span>
                                            )}
                                            {g.total >= 200 && <span style={{ ...premiumBadge, marginLeft: 8 }}>PREMIUM</span>}
                                        </td>
                                        <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{g.steamId ?? "—"}</td>
                                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: g.total >= 200 ? "#ff9933" : "#e2e8f0" }}>{g.total} kr</td>
                                        <td style={{ padding: "10px 16px", textAlign: "right", color: "rgba(255,255,255,0.6)" }}>{g.donations.length}</td>
                                    </tr>
                                    {isOpen && g.donations.map((d) => (
                                        <tr key={d.id} style={{ background: "rgba(20,20,20,0.6)", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                                            {editingId === d.id ? (
                                                <td colSpan={6} style={{ padding: "12px 16px" }}>
                                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                                        <input style={{ ...inputStyle, width: 180 }} value={editSteamId} onChange={(e) => setEditSteamId(e.target.value)} placeholder="SteamID (optional)" />
                                                        <input style={{ ...inputStyle, width: 180 }} value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" />
                                                        <input style={{ ...inputStyle, width: 110 }} type="number" min={1} value={editAmount} onChange={(e) => setEditAmount(e.target.value)} placeholder="Amount" />
                                                        <input style={{ ...inputStyle, width: 150 }} type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                                                        <button className="btn btn-sm btn-warning text-dark fw-bold" disabled={busy} onClick={() => submitEdit(d.id)}>Save</button>
                                                        <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setEditingId(null)}>Cancel</button>
                                                    </div>
                                                </td>
                                            ) : (
                                                <>
                                                    <td />
                                                    <td />
                                                    <td style={{ padding: "8px 16px", color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{d.name}</td>
                                                    <td style={{ padding: "8px 16px", color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
                                                        {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "—"}
                                                    </td>
                                                    <td style={{ padding: "8px 16px", textAlign: "right", color: "rgba(255,255,255,0.85)" }}>{d.donationAmount} kr</td>
                                                    <td style={{ padding: "8px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                                                        <button className="btn btn-sm btn-outline-warning me-2" onClick={() => startEdit(d)}>Edit</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => remove(d.id, d.name)}>Delete</button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>No donations match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <TeamTierSection />
        </div>
    );
};

export default AdminDonatorsPage;
